// Demo admin account for platform demonstrations
import { isAdminRole } from "@/lib/utils/user-roles";
import type { Nullable } from "@/types";
import { UserRole } from "@/types/user-roles";

export interface DemoUser {
  id: string;
  email: string;
  password: string; // Clerk password for automatic login
  firstName: string;
  lastName: string;
  role: UserRole;
  permissions: string[];
  avatar?: string;
  title?: string;
  department?: string;
}

export const DEMO_ADMIN: DemoUser = {
  id: "demo-admin-001",
  email: "admin@lyfe-demo.com",
  password: "admin@lyfe-demo.coM",
  firstName: "Dr. Sarah",
  lastName: "Chen",
  role: UserRole.ADMIN,
  title: "Chief Medical Officer",
  department: "Administration",
  avatar: "/placeholder-user.jpg",
  permissions: [
    // Full platform access
    "admin:all",
    "patients:read",
    "patients:write",
    "patients:delete",
    "analytics:read",
    "clinical-trials:read",
    "clinical-trials:manage",
    "users:read",
    "users:write",
    "settings:read",
    "settings:write",
    "reports:generate",
    "audit:read",
    // Healthcare specific permissions
    "medical-records:read",
    "medical-records:write",
    "prescriptions:read",
    "prescriptions:write",
    "lab-results:read",
    "care-plans:read",
    "care-plans:write",
    "scheduling:read",
    "scheduling:write",
    "billing:read",
    "quality-measures:read",
  ],
};

export const DEMO_PROVIDER: DemoUser = {
  id: "demo-provider-001",
  email: "provider@lyfe-demo.com",
  password: "provider@lyfe-demo.coM",
  firstName: "Dr. Michael",
  lastName: "Rodriguez",
  role: UserRole.PROVIDER,
  title: "Primary Care Physician",
  department: "Internal Medicine",
  avatar: "/placeholder-user.jpg",
  permissions: [
    "patients:read",
    "patients:write",
    "medical-records:read",
    "medical-records:write",
    "prescriptions:read",
    "prescriptions:write",
    "lab-results:read",
    "care-plans:read",
    "care-plans:write",
    "scheduling:read",
    "analytics:read",
  ],
};

export const DEMO_STAFF: DemoUser = {
  id: "demo-staff-001",
  email: "staff@lyfe-demo.com",
  password: "staff@lyfe-demo.coM",
  firstName: "Jennifer",
  lastName: "Park",
  role: UserRole.STAFF,
  title: "Medical Assistant",
  department: "Patient Care",
  avatar: "/placeholder-user.jpg",
  permissions: ["patients:read", "scheduling:read", "scheduling:write", "basic-records:read"],
};

export const DEMO_PATIENT: DemoUser = {
  id: "demo-patient-001",
  email: "brunozhang@example.com",
  password: "BrunoZhang2025!",
  firstName: "Bruno",
  lastName: "Zhang",
  role: UserRole.PATIENT,
  title: "Patient",
  department: "Self-Service",
  avatar: "/placeholder-user.jpg",
  permissions: ["patient-portal:read", "patient-portal:write"],
};

export const DEMO_FAMILY: DemoUser = {
  id: "demo-family-001",
  email: "family@lyfe-demo.com",
  password: "family@lyfe-demo.coM",
  firstName: "Mary",
  lastName: "Johnson",
  role: UserRole.FAMILY,
  title: "Family Caregiver",
  department: "Family Care",
  avatar: "/placeholder-user.jpg",
  permissions: ["family-portal:read", "family-portal:write", "patient-portal:read"],
};

/**
 * Get demo user by role
 */
export function getDemoUser(role: UserRole = UserRole.ADMIN): DemoUser {
  switch (role) {
    case UserRole.ADMIN:
      return DEMO_ADMIN;
    case UserRole.PROVIDER:
      return DEMO_PROVIDER;
    case UserRole.STAFF:
      return DEMO_STAFF;
    case UserRole.PATIENT:
      return DEMO_PATIENT;
    case UserRole.FAMILY:
      return DEMO_FAMILY;
    default:
      return DEMO_ADMIN;
  }
}

/**
 * Check if user has specific permission
 */
export function hasPermission(user: DemoUser, permission: string): boolean {
  // Admin has all permissions
  if (isAdminRole(user.role) || user.permissions.includes("admin:all")) {
    return true;
  }

  return user.permissions.includes(permission);
}

/**
 * Get user display name
 */
export function getUserDisplayName(user: DemoUser): string {
  return `${user.firstName} ${user.lastName}`;
}

/**
 * Get user full title
 */
export function getUserFullTitle(user: DemoUser): string {
  const name = getUserDisplayName(user);
  const title = user.title ? `, ${user.title}` : "";
  const department = user.department ? ` - ${user.department}` : "";
  return `${name}${title}${department}`;
}

/**
 * Set demo user session (for development/demo purposes)
 */
export function setDemoUserSession(_user: DemoUser) {
  if (typeof window !== "undefined") {
    // Store in sessionStorage for demo purposes
    sessionStorage.setItem("demo-auth", "true");
  }
}

/**
 * Get current demo user session
 */
export function getDemoUserSession(): Nullable<DemoUser> {
  if (typeof window !== "undefined") {
    const demoAuth = sessionStorage.getItem("demo-auth");
    const demoUser = sessionStorage.getItem("demo-user");

    if (demoAuth === "true" && demoUser) {
      try {
        return JSON.parse(demoUser) as DemoUser;
      } catch (_e) {}
    }
  }
  return null;
}

/**
 * Clear demo user session
 */
export function clearDemoUserSession() {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("demo-user");
    sessionStorage.removeItem("demo-auth");
  }
}

/**
 * Check if currently in demo mode
 */
export function isDemoMode(): boolean {
  if (typeof window !== "undefined") {
    return sessionStorage.getItem("demo-auth") === "true";
  }
  return false;
}
